import React from 'react';
import '../../validation.scss';
import '../../field.scss';
import InputLabel from '../inputLabel/InputLabel';

export type TextAreaProps = {
    /** The name reference to the component */
    name: string;
    /** Additional styles to wrap onto the component */
    className?: string;
    /** An error message to display under the component */
    error?: string;
    /** The label that appears above the component */
    label?: string;
    /** The temporary text before a value is entered into the component */
    placeholder?: string;
    /** The current value of the component */
    value?: string;
    /** Decides if component has floating labels or not */
    floatingLabel?: boolean;
    /** Decides if the component is editable */
    readonly?: boolean;
    /** The amount of rows tall the component should be */
    rows?: number;
    /** The maximum character length */
    maxLength?: number;
    /** A function to be called when bluring out of the component */
    onLeave?: (
        event: React.FormEvent<HTMLTextAreaElement>,
        key?: string
    ) => void;
    /** A function to specify what should happen when the component is focused */
    onFocus?: (event: React.FormEvent<HTMLTextAreaElement>) => void;
    /** A function to be called on every key stroke to pass the value back up to it's controller */
    onChange?: (event: React.FormEvent<HTMLTextAreaElement>) => void;
};

/** A draggable text area that can be filled with text, with or without a limit */
const TextArea = ({
    name,
    className,
    error,
    label,
    placeholder,
    value,
    floatingLabel,
    readonly,
    rows = 4,
    maxLength = 1028,
    onLeave,
    onFocus,
    onChange
}: TextAreaProps) => {
    let styleWrapper = 'form-group';
    styleWrapper += error ? ' has-error' : '';

    let inputStyle = 'field';
    inputStyle += className ? ` ${className}` : '';

    let textAreaInnerStyle = 'form-control';
    if (floatingLabel && label) {
        textAreaInnerStyle += error ? ' is-invalid' : '';
    }

    const heightOffRows = rows * 30;

    const createLabel = (): JSX.Element => {
        if (label !== undefined) {
            return <InputLabel labelText={label} name={name} />;
        }
        return <></>;
    };

    const onChangeInput = (event: React.FormEvent<HTMLTextAreaElement>) => {
        onChange && onChange(event);
    };

    let inputControl: JSX.Element = <></>;
    inputControl = (
        <textarea
            id={name}
            className={textAreaInnerStyle}
            style={{ height: heightOffRows }}
            placeholder={placeholder || label}
            name={name}
            value={value}
            onChange={onChangeInput}
            onFocus={onFocus}
            onBlur={onLeave}
            maxLength={maxLength}
            readOnly={readonly}
        />
    );

    const labelControl = createLabel();

    const createFinalControl = () => {
        if (floatingLabel && label) {
            let wrapper = 'form-floating field';
            let errorColourStyle = '';

            if (error) {
                wrapper += ' has-error ';
                errorColourStyle = 'floating-label-validation-color';
            }

            return (
                <div>
                    <div className={wrapper}>
                        {inputControl}
                        <label htmlFor={name}>{label}</label>
                    </div>
                    <div className={errorColourStyle}>{error}</div>
                </div>
            );
        } else {
            return (
                <div className={styleWrapper}>
                    {labelControl}
                    <div className={inputStyle}>{inputControl}</div>
                    {error && <div className='input'>{error}</div>}
                </div>
            );
        }
    };

    const finalControl = createFinalControl();

    return <>{finalControl}</>;
};

export default TextArea;
